import React, { useRef, useState, useEffect, useMemo, useContext, useCallback } from "react";
import { Selection } from 'prosemirror-state';
import { useEditor, EditorContent, ReactRenderer, ReactNodeViewRenderer } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import Underline from "@tiptap/extension-underline";
import Strike from "@tiptap/extension-strike";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
// eslint-disable-next-line no-unused-vars
import Highlight from "@tiptap/extension-highlight";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import PageBreak from "./PageBreak";
import TextStyle from "@tiptap/extension-text-style";
import { Extension, Node, Mark, mergeAttributes } from '@tiptap/core';
import '../TextEditor.css';
// eslint-disable-next-line no-unused-vars
import { IconBold, IconItalic, IconUnderline, IconStrikethrough, IconList, IconListNumbers, IconH1, IconH2, IconAlignLeft, IconAlignCenter, IconAlignRight, IconAlignJustified, IconHighlight, IconX, IconColorPicker, IconLineHeight } from '@tabler/icons-react';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Youtube from '@tiptap/extension-youtube';
// eslint-disable-next-line no-unused-vars
import ResizableImage from './ResizableImage';
import ImageNodeView from './ImageNodeView';
import ResizableVideo from './ResizableVideo';
import ResizablePDF from './ResizablePDF';
import ResizableYoutube from './ResizableYoutube';
import { QuizNode } from '../extensions/QuizNode';
import { AudioNode } from '../extensions/AudioNode';
import { ABCDExerciseNode } from '../extensions/ABCDExerciseNode';
import OpenQuestionsNode from '../extensions/OpenQuestionsNode';
import WordDefinitionNode from '../extensions/WordDefinitionNode';
import WordTranslationNode from '../extensions/WordTranslationNode';
import FillInTheGapsNode from '../extensions/FillInTheGapsNode';
import TrueFalseNode from '../extensions/TrueFalseNode';
import WarmUpDiscussionNode from '../extensions/WarmUpDiscussionNode';
import CreateDialogueNode from '../extensions/CreateDialogueNode';
import EssentialVocabularyNode from '../extensions/EssentialVocabularyNode';
import LeadInActivitiesNode from '../extensions/LeadInActivitiesNode';
import FourOpinionsNode from '../extensions/FourOpinionsNode';
import CommunicativeSituationsNode from '../extensions/CommunicativeSituationsNode';
import { ProsConsNode } from '../extensions/ProsConsNode';
import { MatchingHalvesNode } from '../extensions/MatchingHalvesNode';
import ErrorCorrectionNode from '../extensions/ErrorCorrectionNode';
import GrammarRulesNode from '../extensions/GrammarRulesNode';
import JumbledSentenceNode from '../extensions/JumbledSentenceNode';
import MultipleChoiceNode from '../extensions/MultipleChoiceNode';
import { ActiveEditorContext } from '../context/ActiveEditorContext';
import TablePopup from './TablePopup';

// eslint-disable-next-line no-unused-vars
import { FONT_FAMILIES } from '../assets/fonts';

// Функции для сжатия файлов
const compressImage = (base64String, maxWidth = 800, quality = 0.6) => {
  return new Promise((resolve) => {
            const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Вычисляем новые размеры
      let { width, height } = img;
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Рисуем изображение с новыми размерами
      ctx.drawImage(img, 0, 0, width, height);
      
      // Конвертируем в base64 с заданным качеством
      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      console.log('Image compressed from', base64String.length, 'to', compressedBase64.length, 'characters');
      resolve(compressedBase64);
    };
    img.src = base64String;
  });
};

const compressAudio = (base64String) => {
  return new Promise((resolve) => {
    // Для аудио файлов больше 1MB, предлагаем пользователю использовать меньший файл
    console.log('Audio file size:', base64String.length, 'characters');
    if (base64String.length > 1024 * 1024) { // Если больше 1MB
      console.warn('Audio file is very large, consider using a smaller file');
      // Показываем предупреждение пользователю
      alert('Warning: This audio file is very large and may cause storage issues. Consider using a smaller audio file (under 1MB).');
      // Возвращаем null, чтобы не сохранять слишком большой файл
      resolve(null);
    } else {
      resolve(base64String);
    }
  });
};

// Кастомное расширение для хайлатера с цветами
const CustomHighlight = Mark.create({
  name: 'highlight',
  
  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      color: {
        default: '#fef3c7',
        parseHTML: element => element.getAttribute('data-color'),
        renderHTML: attributes => {
          if (!attributes.color) {
            return {}
          }
          return {
            'data-color': attributes.color,
            style: `background-color: ${attributes.color}`,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'mark',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },

  addCommands() {
    return {
      setHighlight: attributes => ({ commands }) => {
        return commands.setMark(this.name, attributes)
      },
      unsetHighlight: () => ({ commands }) => {
        return commands.unsetMark(this.name)
      },
      toggleHighlight: attributes => ({ commands }) => {
        return commands.toggleMark(this.name, attributes)
      },
    }
  },
})

// eslint-disable-next-line no-unused-vars
const FONT_SIZES = ['12px', '14px', '16px', '18px', '24px', '32px'];

// Custom FontFamily extension
const FontFamily = Extension.create({
  name: 'fontFamily',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontFamily: {
            default: null,
            parseHTML: element => element.style.fontFamily,
            renderHTML: attributes => {
              if (!attributes.fontFamily) return {}
              return {
                style: `font-family: ${attributes.fontFamily}`,
              }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontFamily: fontFamily => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontFamily })
          .run()
      },
    }
  },
})

// Custom FontSize extension
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize,
            renderHTML: attributes => {
              if (!attributes.fontSize) return {}
              return {
                style: `font-size: ${attributes.fontSize}`,
              }
            },
          },
        },
      },
    ]
  },
  addCommands() {
    return {
      setFontSize: fontSize => ({ chain }) => {
        return chain()
          .setMark('textStyle', { fontSize })
          .run()
      },
    }
  },
})

const FileEmbed = Node.create({
  name: 'fileEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      url: { default: null },
      filename: { default: null },
      type: { default: null },
      width: { default: '100%' },
      height: { default: '400' },
    };
  },
  parseHTML() {
    return [
      { tag: 'div[data-type="file-embed"]' },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    if (HTMLAttributes.type === 'pdf') {
      return [
        'div',
        { ...HTMLAttributes, 'data-type': 'file-embed', style: 'display:flex;flex-direction:column;align-items:center;margin:0;' },
        [
          'iframe',
          {
            src: (HTMLAttributes.url || '') + '#toolbar=0&navpanes=0&scrollbar=0',
            width: HTMLAttributes.width || '100%',
            height: HTMLAttributes.height || '400',
            style: 'border:1.5px solid #e0e0e0;border-radius:12px;box-shadow:0 2px 12px #8a2be222;margin-bottom:8px;',
            allow: 'autoplay',
          },
        ],
        [
          'a',
          { href: HTMLAttributes.url, target: '_blank', rel: 'noopener noreferrer', style: 'color:#8a2be2;font-weight:500;text-decoration:underline;' },
          HTMLAttributes.filename || 'Open PDF'
        ]
      ];
    } else if (HTMLAttributes.type === 'docx') {
      return [
        'div',
        { ...HTMLAttributes, 'data-type': 'file-embed', style: 'display:flex;align-items:center;margin:0;' },
        [
          'span', { style: 'font-size:22px;margin-right:8px;' }, '📄' ],
        [
          'a',
          { href: HTMLAttributes.url, target: '_blank', rel: 'noopener noreferrer', style: 'color:#8a2be2;font-weight:500;text-decoration:underline;' },
          HTMLAttributes.filename || 'Download DOCX'
        ]
      ];
    }
    return ['div', {}, 'Unsupported file'];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizablePDF);
  },
  addCommands() {
    return {
      insertFileEmbed: ({ url, filename, type, width, height }) => ({ commands }) => {
        return commands.insertContent({ type: this.name, attrs: { url, filename, type, width, height } });
      },
    };
  },
});

const toolbarBtn = {
  base: {
    background: 'none',
    border: 'none',
    borderRadius: 10,
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    margin: 0,
    padding: 0,
    color: '#6b7280',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
  },
  active: {
    background: 'linear-gradient(135deg, #8a2be2 0%, #a855f7 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(112, 112, 112, 0.25)',
  },
  hover: {
    background: 'linear-gradient(135deg, #f3eafd 0%, #ede2f9 100%)',
    color: '#8a2be2',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(112, 112, 112, 0.15)',
  }
};

// eslint-disable-next-line no-unused-vars
const toolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(112, 112, 112, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(255, 255, 255, 0.8)',
  padding: '8px 12px',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
};

// eslint-disable-next-line no-unused-vars
const separatorStyle = {
  width: 1,
  height: 24,
  background: 'linear-gradient(180deg, transparent 0%, rgba(138, 43, 226, 0.2) 50%, transparent 100%)',
  margin: '0 4px',
};

// Cache key for the "image_size_*" localStorage restore below and in
// ImageNodeView.jsx's saveImageSize/loadImageSize (must derive keys
// identically in both places, or a size saved by one is never found by the
// other). Hashes the FULL src rather than truncating to a short prefix —
// every JPEG data URL this app produces via canvas.toDataURL() starts with
// near-identical header bytes (same encoder, same fixed quality), so a
// short-prefix key routinely collided between totally unrelated images,
// applying one image's cached width/height to another's node.
const hashImageSrc = (src) => {
  const s = String(src || '');
  let hash = 5381;
  for (let i = 0; i < s.length; i++) {
    hash = ((hash << 5) + hash + s.charCodeAt(i)) | 0;
  }
  return (hash >>> 0).toString(36);
};

// Custom Image extension with resize and drag
const CustomImage = Image.extend({
  group: 'block',
  inline: false,
  atom: true,
  addOptions() {
    return {
      ...this.parent?.(),
      isStudent: false,
    };
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      // Stable identity for this specific image node, independent of its
      // position in the document. ProseMirror's NodeView reuse during a
      // full editor.commands.setContent() replace (how live-sync applies
      // a peer's update) matches atom nodes like images purely by TYPE +
      // DOM-array POSITION — see CustomImage.addNodeView()'s update()
      // below, which now checks this id before accepting a reuse. Without
      // it, inserting a new image mid-document shifts every later
      // sibling's index, and ProseMirror can silently hand one image's
      // data to a NodeView instance (canvas, refs, local state) that
      // actually belongs to a DIFFERENT image — which is what made
      // drawing on a freshly-inserted image flash in and then revert,
      // while drawing on an image present since the container's initial
      // mount (whose position never shifts) worked fine.
      imageId: {
        default: null,
        // MUST be deterministic, not random: every image saved before this
        // attribute existed has no data-image-id in its stored HTML, so
        // this fallback runs independently in EVERY browser that parses
        // that same stored content — teacher and student each parsing the
        // identical HTML from the same DB row. A random fallback (the
        // first version of this fix) meant they'd generate two DIFFERENT
        // ids for what is definitely the same logical image, so the
        // mismatch check below would ALWAYS fire on the very next sync —
        // not just for genuinely different images. Hashing the src (the
        // one thing guaranteed identical across both parses) makes both
        // sides derive the same fallback id for the same legacy image.
        parseHTML: element => element.getAttribute('data-image-id') || `img_${hashImageSrc(element.getAttribute('src') || '')}`,
        renderHTML: attributes => {
          if (!attributes.imageId) return {};
          return { 'data-image-id': attributes.imageId };
        },
      },
      width: {
        default: null,
        parseHTML: element => {
          const width = element.getAttribute('width');
          return width;
        },
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: null,
        parseHTML: element => {
          const height = element.getAttribute('height');
          return height;
        },
        renderHTML: attributes => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
      alt: {
        default: '',
        parseHTML: element => element.getAttribute('alt'),
        renderHTML: attributes => {
          if (!attributes.alt) return {};
          return { alt: attributes.alt };
        },
      },
      align: {
        default: null,
        parseHTML: element => element.getAttribute('data-align'),
        renderHTML: attributes => {
          if (!attributes.align) return {};
          return { 'data-align': attributes.align };
        },
      },
      textElements: {
        default: null,
        parseHTML: element => element.getAttribute('data-text-elements') || null,
        renderHTML: attributes => {
          if (!attributes.textElements) return {};
          return { 'data-text-elements': attributes.textElements };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'img',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const align = HTMLAttributes.align;
    let wrapperStyle = 'margin: 0; padding: 0;';
    if (align === 'center') wrapperStyle += 'text-align:center;';
    if (align === 'left') wrapperStyle += 'text-align:left;';
    if (align === 'right') wrapperStyle += 'text-align:right;';
    return [
      'div',
      { style: wrapperStyle },
      ['img', { ...HTMLAttributes, style: 'margin: 0; padding: 0; display: block;' }]
    ];
  },
  addNodeView() {
    return ({ node, updateAttributes, editor, getPos }) => {
      // ProseMirror's raw addNodeView() factory never actually supplies an
      // updateAttributes function (its NodeView constructor args are
      // {node, view, getPos, decorations, innerDecorations, editor,
      // extension, HTMLAttributes} — verified against @tiptap/core), so
      // the fallback below always runs. It used to close over `node` from
      // this outer scope — the node this NodeView instance was
      // constructed with, never reassigned as the doc changes. Since
      // ProseMirror REUSES this same NodeView instance (calling update()
      // below) rather than recreating it whenever editor.commands.
      // setContent() finds an equivalent node in the same slot — which is
      // exactly what happens every time a remote drawing/text update gets
      // applied — every later safeUpdateAttributes() call (e.g. the
      // width/height restore in ImageNodeView's <img onLoad>) silently
      // merged its patch against that STALE mount-time node.attrs,
      // discarding any attribute that had changed since mount — most
      // importantly `src`. That's what made a peer's live drawing update
      // flash in and then immediately revert. currentNode is kept in sync
      // by update() below so this always merges against the doc's actual
      // current attrs.
      let currentNode = node;
      let safeUpdateAttributes = updateAttributes;
      if (typeof safeUpdateAttributes !== 'function') {
        safeUpdateAttributes = attrs => {
          const pos = getPos();
          if (typeof pos === 'number') {
            editor.chain().focus().command(({ tr }) => {
              tr.setNodeMarkup(pos, undefined, { ...currentNode.attrs, ...attrs });
              return true;
            }).run();
          } else {
            editor.commands.updateAttributes('image', attrs);
          }
        };
      }
      let deleteNode = () => {
        const pos = getPos();
        if (typeof pos === 'number') {
          editor.chain().focus().deleteRange({ from: pos, to: pos + currentNode.nodeSize }).run();
        }
      };
      const reactRenderer = new ReactRenderer(ImageNodeView, {
        props: { key: node.attrs.imageId, node, updateAttributes: safeUpdateAttributes, selected: false, editor, getPos, deleteNode, isStudent: this.options.isStudent },
        editor,
      });
      // Listen to selection changes and update selected prop
      const updateSelected = () => {
        const pos = getPos();
        const sel = editor.state.selection;
        const isSelected = sel.from <= pos && sel.to >= pos + currentNode.nodeSize - 1;
        reactRenderer.updateProps({
          key: currentNode.attrs.imageId,
          selected: isSelected,
          node: currentNode,
          updateAttributes: safeUpdateAttributes,
          editor,
          getPos,
          deleteNode,
        });
      };
      editor.on('selectionUpdate', updateSelected);
      return {
        dom: reactRenderer.element,
        destroy: () => {
          reactRenderer.destroy();
          editor.off('selectionUpdate', updateSelected);
        },
        update: (newNode) => {
          // ProseMirror reconciles atom nodes like images by TYPE +
          // DOM-array POSITION ONLY (verified against prosemirror-view's
          // ViewTreeUpdater/updateNextNode) — it has no concept of "this
          // logical image" vs "that logical image". Inserting a new
          // image mid-document shifts every later sibling's position-
          // index, and on that tick ProseMirror can silently hand THIS
          // instance's canvas/refs/local state to a completely different
          // logical image — which is what made drawing on a freshly-
          // inserted image (but not one present since the container's
          // initial mount, whose position never shifts) flash in and
          // then revert.
          //
          // An earlier version of this fix returned `false` here on an
          // imageId mismatch to make ProseMirror reject the reuse — but
          // for atom nodes (images are `atom: true`) that path always
          // falls through to a full destroy+remount (recreateWrapper, the
          // cheaper alternative, unconditionally bails for atoms — see
          // prosemirror-view's updateNextNode), which turned out to fire
          // on nearly every cross-peer sync and drop in-flight local
          // state (an active drawing session, a pending attrs update)
          // each time. Passing `key: imageId` into the React props
          // instead lets REACT do the "different logical image, reset
          // everything" work via its own standard key-based remount —
          // ReactRenderer.render() forwards props straight into
          // React.createElement(), which honors `key` specially — while
          // ProseMirror's own DOM/doc bookkeeping (this NodeView's outer
          // `dom` element) never changes at all, sidestepping the
          // destroy+remount cost and its dropped-state window entirely.
          currentNode = newNode;
          reactRenderer.updateProps({
            key: newNode.attrs.imageId,
            node: newNode,
            updateAttributes: safeUpdateAttributes,
            editor,
            getPos,
            deleteNode,
          });

          // Гарантируем, что размеры изображения не потеряются
          try {
            const src = newNode?.attrs?.src;
            const width = newNode?.attrs?.width;
            const height = newNode?.attrs?.height;
            if (src && (!width || !height || width === 'auto' || height === 'auto')) {
              const key = `image_size_${hashImageSrc(src)}`;
              const saved = localStorage.getItem(key);
              if (saved) {
                const { width: savedW, height: savedH } = JSON.parse(saved);
                const pos = getPos();
                if (savedW && savedH && typeof pos === 'number') {
                  editor.chain().command(({ tr }) => {
                    tr.setNodeMarkup(pos, undefined, { ...newNode.attrs, width: savedW, height: savedH });
                    return true;
                  }).run();
                }
              }
            }
          } catch (_) {
            // no-op
          }
          return true;
        },
      };
    };
  },
});

// Custom Video extension
const ResizableVideoExtension = Node.create({
  name: 'resizableVideo',
  group: 'block',
  inline: false,
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: element => element.getAttribute('src'),
        renderHTML: attributes => {
          if (!attributes.src) return {};
          return { src: attributes.src };
        },
      },
      width: {
        default: '400px',
        parseHTML: element => element.getAttribute('width'),
        renderHTML: attributes => {
          if (!attributes.width) return {};
          return { width: attributes.width };
        },
      },
      height: {
        default: 'auto',
        parseHTML: element => element.getAttribute('height'),
        renderHTML: attributes => {
          if (!attributes.height) return {};
          return { height: attributes.height };
        },
      },
      controls: {
        default: true,
        parseHTML: element => element.hasAttribute('controls'),
        renderHTML: attributes => {
          if (!attributes.controls) return {};
          return { controls: 'controls' };
        },
      },
      align: {
        default: 'center',
        parseHTML: element => element.getAttribute('data-align'),
        renderHTML: attributes => {
          if (!attributes.align) return {};
          return { 'data-align': attributes.align };
        },
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'video',
      },
      {
        tag: 'resizableVideo',
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['video', HTMLAttributes];
  },
  addCommands() {
    return {
      setResizableVideo: (options) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: options,
        });
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableVideo);
  },
});

// The only nested-NodeView input in this app that steals native DOM focus
// away from ProseMirror's own contentEditable region while editing content
// that still belongs to THIS editor — ImageNodeView's text-on-image field
// (className "image-text-input"). editor.isFocused can't see it (it only
// tracks view.dom), so the live-content sync below checks for it directly
// rather than relying on isFocused alone.
const isTypingInNestedInput = () => {
  try {
    return !!document.activeElement?.classList?.contains('image-text-input');
  } catch (_) {
    return false;
  }
};

const TextEditor = ({ id, setMainEditor, addEditorToMap, initialContent, onContentChange, isStudent = false, lessonMode = 'edit', liveContent = null, isStudentLocked = false }) => {
  const { setActiveEditor } = useContext(ActiveEditorContext);
  // eslint-disable-next-line no-unused-vars
  const [currentAlignment, setCurrentAlignment] = useState('left');
  // eslint-disable-next-line no-unused-vars
  const [currentLineSpacing, setCurrentLineSpacing] = useState('normal');
  const [tablePopup, setTablePopup] = useState(null);
  const contentSetRef = useRef(false);
  const lastContentRef = useRef('');
  const editorAddedRef = useRef(false);
  const isSettingContentRef = useRef(false);
  const lastInitialContentRef = useRef('');
  const addEditorToMapRef = useRef(addEditorToMap);
  // Set right before a tap/click on non-text UI inside an exercise embed
  // (contentEditable=false subtree). If the editor root ends up focused
  // anyway — however that happens — the `focus` handleDOMEvents hook below
  // immediately blurs it back off, so the on-screen keyboard never gets a
  // chance to stay open for a tap that was never real text entry.
  const suppressEditorFocusRef = useRef(false);

  // console.log(`TextEditor ${id} - initialContent:`, initialContent);

  // Обновляем ref для addEditorToMap при изменении
  useEffect(() => {
    addEditorToMapRef.current = addEditorToMap;
  }, [addEditorToMap]);

  // Memoize the initial content to prevent unnecessary editor recreation
  const memoizedInitialContent = useMemo(() => {
    // Сравниваем с предыдущим значением
    if (lastInitialContentRef.current !== initialContent) {
      lastInitialContentRef.current = initialContent;
    }
    return lastInitialContentRef.current;
  }, [initialContent]);

  // Line spacing options
  // eslint-disable-next-line no-unused-vars
  const lineSpacingOptions = [
    { value: '1', label: '1.0' },
    { value: '1.15', label: '1.15' },
    { value: '1.5', label: '1.5' },
    { value: '2', label: '2.0' },
  ];

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bold: false,
        italic: false,
        strike: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        horizontalRule: false,
        textStyle: false,
        dropcursor: { color: '#dee2e6', width: 1 },
      }),
      TextStyle,
      FontFamily,
      FontSize,
      Color,
              CustomHighlight,
      Heading.configure({ levels: [1, 2] }),
      Bold,
      Italic,
      Underline,
      Strike,
      BulletList,
      OrderedList,
      ListItem,
      TextAlign.configure({ types: ['heading', 'paragraph', 'bulletList', 'orderedList'] }),
      Table.configure({ resizable: true, lastColumnResizable: false, cellMinWidth: 48 }),
      TableRow,
      TableCell,
      TableHeader,
      TaskList,
      TaskItem.configure({
        onBackspace: ({ editor }) => {
          // При Backspace в пустом task item - удаляем его
          return false;
        },
        onDelete: ({ editor }) => {
          // При Delete в пустом task item - удаляем его
          return false;
        },
        onTab: ({ editor }) => {
          // При Tab - переходим к следующему task item
          return false;
        },
        onShiftTab: ({ editor }) => {
          // При Shift+Tab - переходим к предыдущему task item
          return false;
        },
        onArrowUp: ({ editor }) => {
          // При стрелке вверх - переходим к предыдущему task item
          return false;
        },
        onArrowDown: ({ editor }) => {
          // При стрелке вниз - переходим к следующему task item
          return false;
        },
        onEnter: ({ editor }) => {
          // При Enter в пустом task item - создаем обычный параграф
          const { state } = editor;
          const { selection } = state;
          const { $from } = selection;
          
          // Проверяем, что мы в пустом task item
          if ($from.parent.content.size === 0) {
            // Создаем обычный параграф вместо нового task item
            editor.chain().focus().setNode('paragraph').run();
            return true;
          }
          
          return false;
        }
      }),
      HorizontalRule,
      PageBreak,
      CustomImage.configure({ isStudent }),
      Link,
      Youtube.extend({
        draggable: true,
        // The stock extension has no interactive view at all — no resize
        // handle, and draggable:true alone doesn't work reliably once an
        // <iframe> covers the node (same issue fixed for audio embeds:
        // ProseMirror needs a clear drag origin, and here the iframe is its
        // own browsing context so clicks on the video itself never even
        // reach the parent document). ResizableYoutube adds a resize handle
        // and data-drag-handle, matching ResizableVideo/ResizablePDF.
        addNodeView() {
          return ReactNodeViewRenderer(ResizableYoutube);
        },
      }).configure({
        inline: false,
        modestBranding: true,
        rel: false,
        width: 560,
        height: 315,
        enableIFrameApi: true,
        origin: window.location.origin,
      }),
      FileEmbed,
      ResizableVideoExtension,
      QuizNode,
      AudioNode,
      ABCDExerciseNode,
      OpenQuestionsNode,
      WordDefinitionNode,
      WordTranslationNode,
      FillInTheGapsNode,
      TrueFalseNode,
      WarmUpDiscussionNode,
      CreateDialogueNode,
      EssentialVocabularyNode,
      LeadInActivitiesNode,
      FourOpinionsNode,
      CommunicativeSituationsNode,
      ProsConsNode,
      MatchingHalvesNode,
      ErrorCorrectionNode,
      GrammarRulesNode,
      JumbledSentenceNode,
      MultipleChoiceNode,
    ],
    content: memoizedInitialContent || "",
    editorProps: {
      attributes: {
        class: `prose prose-sm sm:prose-lg xl:prose-2xl mx-auto focus:outline-none`,
        'data-editor-id': String(id),
      },
      handlePaste: (view, event, slice) => {
        // Обработка вставки файлов
        const items = Array.from(event.clipboardData?.items || []);
        const files = items.filter(item => item.kind === 'file');
        
        if (files.length > 0) {
          event.preventDefault();
          
          files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target.result;
              
              // Определяем тип файла и обрабатываем соответственно
              if (file.type.startsWith('image/')) {
                // Сжимаем изображение
                compressImage(base64).then(compressedImage => {
                  editor.chain().focus().insertContent({
                    type: 'image',
                    attrs: { src: compressedImage, imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}` }
                  }).run();
                });
              } else if (file.type.startsWith('audio/')) {
                // Проверяем размер файла перед обработкой
                if (file.size > 1024 * 1024) { // Если больше 1MB
                  alert('Audio file is too large. Please use a file smaller than 1MB.');
                  return;
                }
                
                const reader = new FileReader();
                reader.onload = () => {
                  const base64 = reader.result;
                  // Сжимаем аудио
                  compressAudio(base64).then(compressedAudio => {
                    if (compressedAudio) {
                      editor.chain().focus().insertContent({
                        type: 'audioNode',
                        attrs: { src: compressedAudio }
                      }).run();
                    }
                  });
                };
                reader.readAsDataURL(file);
              }
            };
            reader.readAsDataURL(file);
          });
          
          return true;
        }
        
        return false;
      },
      handleDrop: (view, event, slice, moved) => {
        // Обработка перетаскивания файлов И внутренних нод
        const files = Array.from(event.dataTransfer?.files || []);
        // Новые: проверим внутренний перенос ноды NOVAS
        try {
          const raw = event.dataTransfer && event.dataTransfer.getData('application/x-novas-node');
          if (raw) {
            try {
              const payload = JSON.parse(raw);
              if (payload && payload.type === 'grammarRulesExercise') {
                const currentRoot = editor?.view?.dom;
                const currentEditorId = currentRoot?.getAttribute?.('data-editor-id');
                const sourceEditorId = payload.sourceEditorId;
                // Если источник тот же редактор — не перехватываем, даем ProseMirror обработать перемещение
                if (currentEditorId && sourceEditorId && String(currentEditorId) === String(sourceEditorId)) {
                  return false;
                }
                // Иначе — кросс-редакторная вставка
                event.preventDefault();
                const { content, title, level } = payload.attrs || {};
                editor.chain().focus().insertContent({ type: 'grammarRulesExercise', attrs: { content, title, level } }).run();
                return true;
              }
            } catch (_) {}
          }
        } catch (_) {}
        
        if (files.length > 0) {
          event.preventDefault();
          
          files.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const base64 = e.target.result;
              
              // Определяем тип файла и обрабатываем соответственно
              if (file.type.startsWith('image/')) {
                // Сжимаем изображение
                compressImage(base64).then(compressedImage => {
                  editor.chain().focus().insertContent({
                    type: 'image',
                    attrs: { src: compressedImage, imageId: `img_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}` }
                  }).run();
                });
              } else if (file.type.startsWith('audio/')) {
                // Проверяем размер файла перед обработкой
                if (file.size > 1024 * 1024) { // Если больше 1MB
                  alert('Audio file is too large. Please use a file smaller than 1MB.');
                  return;
                }
                
                const reader = new FileReader();
                reader.onload = () => {
                  const base64 = reader.result;
                  // Сжимаем аудио
                  compressAudio(base64).then(compressedAudio => {
                    if (compressedAudio) {
                      editor.chain().focus().insertContent({
                        type: 'audioNode',
                        attrs: { src: compressedAudio }
                      }).run();
                    }
                  });
                };
                reader.readAsDataURL(file);
              }
            };
            reader.readAsDataURL(file);
          });
          
          return true;
        }
        
        return false;
      },
      handleDOMEvents: {
        mousedown: (view, event) => {
          try {
            // Image atoms are the one exception to the guard below: we WANT
            // ProseMirror's default click handling here so it sets a
            // NodeSelection on the image, which is what flips
            // ImageNodeView's `selected` prop and reveals the resize/draw/
            // text toolbar. The image's own NodeView wrapper is also
            // contentEditable=false (same as every atom node), so this must
            // be checked BEFORE the broad guard or it gets swallowed there
            // too. No exercise embed renders <img>/.resizable-image, so this
            // is a no-op for them — safe, unlike the earlier attempt that
            // narrowed the guard itself and broke Matching Halves' plain
            // <div> click targets.
            if (event.target && event.target.closest && event.target.closest('img, .resizable-image')) {
              return false;
            }
            // Clicks inside an interactive node view (anything wrapped in
            // contentEditable=false — exercise embeds, the Quiz card, etc.)
            // must NOT fall through to ProseMirror's default handler. The
            // default sets a NodeSelection on the surrounding atom and
            // scrollIntoView()'s it — which reads as "the page jumps to
            // the bottom" the instant the teacher clicks the exercise's
            // Edit pencil or focuses a textarea inside it. Returning true
            // says "handled, leave it alone." Native React onClick still
            // fires because click follows mousedown regardless of
            // preventDefault. But on mobile Safari, if the tapped target
            // isn't itself natively focusable (a plain button/div — e.g.
            // an answer choice), the browser's default mousedown action
            // falls back to focusing the nearest contentEditable ancestor,
            // which is the outer ProseMirror root — popping the keyboard
            // for a tap that never needed text input. preventDefault stops
            // that fallback. Real inputs/textareas (edit-mode fields that
            // DO need the keyboard) are excluded so they still get native
            // focus.
            if (event.target && event.target.closest && event.target.closest('[contenteditable="false"]')) {
              const isNativeTextTarget = event.target.matches && event.target.matches(
                'input, textarea, select, [contenteditable="true"], [contenteditable=""]'
              );
              // [data-drag-handle] regions are excluded from preventDefault
              // — every exercise embed's draggable-for-reordering wrapper
              // carries that attribute, and preventDefault() on mousedown
              // is exactly what stops the browser from ever starting a
              // native HTML5 drag, which had silently killed drag-to-
              // reorder for every exercise type.
              const isDragHandle = event.target.closest && event.target.closest('[data-drag-handle]');
              if (!isNativeTextTarget && !isDragHandle) {
                event.preventDefault();
                suppressEditorFocusRef.current = true;
                setTimeout(() => { suppressEditorFocusRef.current = false; }, 500);
              }
              return true;
            }
            const coords = { left: event.clientX, top: event.clientY };
            const found = view.posAtCoords(coords);
            if (!found) return false;
            const { state } = view;
            const $pos = state.doc.resolve(found.pos);
            // Если не текстовый блок (щель/ниже атома) — вставим параграф и поставим карет
            if (!$pos.parent || !$pos.parent.isTextblock) {
              const paragraph = state.schema.nodes.paragraph;
              if (paragraph) {
                const tr = state.tr.insert(found.pos, paragraph.create());
                view.dispatch(tr);
                const sel = Selection.near(view.state.doc.resolve(found.pos + 1), 1);
                view.dispatch(view.state.tr.setSelection(sel).scrollIntoView());
                return true;
              }
              return false;
            }
            // Иначе переместим карет ближе к позиции клика
            const sel = Selection.near($pos, 1);
            view.dispatch(state.tr.setSelection(sel));
          } catch (_) {}
          return false;
        },
        // NOTE: preventDefault() on touchend was tried here and reverted —
        // it silences the synthetic "click" event mobile browsers fire
        // after a tap, which broke opening/selecting inside every exercise
        // embed. touchstart's preventDefault is a no-op too (ProseMirror
        // registers its own touchstart listener with { passive: true }, see
        // passiveHandlers in prosemirror-view, so preventDefault from any
        // custom touchstart handler is silently ignored). So this only
        // tracks "a non-text tap happened inside an exercise embed" for the
        // reactive blur in the focus handler below — it does not, and must
        // not, call preventDefault.
        touchend: (view, event) => {
          try {
            if (event.target && event.target.closest && event.target.closest('img, .resizable-image')) {
              return false;
            }
            if (event.target && event.target.closest && event.target.closest('[contenteditable="false"]')) {
              const isNativeTextTarget = event.target.matches && event.target.matches(
                'input, textarea, select, [contenteditable="true"], [contenteditable=""]'
              );
              if (!isNativeTextTarget) {
                suppressEditorFocusRef.current = true;
                setTimeout(() => { suppressEditorFocusRef.current = false; }, 500);
              }
            }
          } catch (_) {}
          return false;
        },
        // Belt-and-suspenders: whatever the exact mechanism is that hands
        // the editor DOM focus after a tap on non-text exercise UI (an
        // answer choice, a "next question" button, etc.), if it happens
        // right after we flagged that tap as non-text via mousedown/
        // touchstart above, immediately blur it back off. The keyboard has
        // no functional impact when it's up (everything still works) but
        // it eats half the screen for a tap that never needed text entry —
        // this reacts to that focus instead of trying to guess/prevent its
        // exact cause, which proved unreliable on iOS.
        focus: (view) => {
          if (suppressEditorFocusRef.current) {
            suppressEditorFocusRef.current = false;
            requestAnimationFrame(() => {
              try {
                if (document.activeElement === view.dom) view.dom.blur();
              } catch (_) {}
            });
          }
          return false;
        }
      }
    },
    onUpdate: ({ editor }) => {
      const canStudentEdit = isStudent && !isStudentLocked;
      if ((isStudent && !canStudentEdit) || (!isStudent && lessonMode === 'view_only')) return;
      
      const isEmpty = editor.isEmpty;
      const editorElement = editor.view.dom;
      if (isEmpty) {
        editorElement.classList.add('is-empty');
      } else {
        editorElement.classList.remove('is-empty');
      }
      
      // Update current alignment state
      const align = editor.getAttributes('paragraph').textAlign || 'left';
      setCurrentAlignment(align);
      
      // Вызываем callback для сохранения содержимого только если содержимое действительно изменилось
      // и мы не устанавливаем содержимое программно
      if (isSettingContentRef.current) {
        isSettingContentRef.current = false;
        return;
      }
      
      const currentContent = editor.getHTML();
      if (onContentChange && currentContent !== lastContentRef.current) {
        lastContentRef.current = currentContent;
        lastLocalEditAtRef.current = Date.now();
        onContentChange(currentContent);
      }
    },
    onFocus: ({ editor }) => {
      const canStudentEdit = isStudent && !isStudentLocked;
      if (!isStudent ? lessonMode !== 'view_only' : canStudentEdit) {
        setActiveEditor(editor);
      }
    },
    onCreate: ({ editor }) => {
      // expose current tab type for nodeViews (homework, doc, etc.)
      try {
        const root = editor.view.dom;
        if (root && typeof document !== 'undefined') {
          const tabType = document.body.getAttribute('data-novas-current-tab-type');
          if (tabType) root.setAttribute('data-tab-type', tabType);
        }
      } catch (_) {}
      // Set is-empty on init so placeholder is visible before any interaction
      if (editor.isEmpty) {
        editor.view.dom.classList.add('is-empty');
      }
    },
    editable: isStudent ? !isStudentLocked : lessonMode !== 'view_only',
  });

  useEffect(() => {
    if (!editor || !isStudent) return;
    editor.setEditable(!isStudentLocked);
  }, [editor, isStudent, isStudentLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editor) return;
    const applyLightGrayDropCursor = () => {
      const nodes = document.querySelectorAll('.ProseMirror-dropcursor');
      nodes.forEach(el => {
        try {
          el.style.borderTopColor = 'transparent';
          el.style.borderTopStyle = 'none';
          el.style.borderLeftColor = '#ee5a24';
          el.style.borderLeftStyle = 'solid';
          el.style.background = 'transparent';
          el.style.opacity = '0.7';
        } catch {}
      });
    };
    applyLightGrayDropCursor();
    const observer = new MutationObserver(applyLightGrayDropCursor);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [editor]);

  // Добавляем начальный класс для первого контейнера
  // useEffect(() => {
  //   if (editor && id === 1) {
  //     editor.view.dom.classList.add('is-empty');
  //   }
  // }, [editor, id]);

  useEffect(() => {
    if (setMainEditor && editor) setMainEditor(editor);
  }, [editor, setMainEditor]);

  // Обработчик правого клика для таблиц
  useEffect(() => {
    if (!editor) return;

    // Глобальная переменная для хранения состояния выделения
    let savedSelection = null;

    const handleMouseDown = (event) => {
      // Сохраняем выделение при любом клике мыши
      const { state } = editor;
      const { selection } = state;
      const $from = selection.$from;
      const $to = selection.$to;
      
      // Проверяем, что мы в таблице - ищем table node в иерархии
      let tableNode = null;
      let depth = 1;
      while (depth <= 10 && !tableNode) {
        const node = $from.node(-depth);
        if (node && node.type.name === 'table') {
          tableNode = node;
          break;
        }
        depth++;
      }
      
      if (tableNode) {
        // Сохраняем состояние только если есть выделение или мы в ячейке с текстом
        const hasSelection = $from.pos !== $to.pos;
        const hasText = $from.node() && $from.node().textContent && $from.node().textContent.trim() !== '';
        
        // Проверяем, что мы находимся в табличной ячейке
        const isInTableCell = $from.node().type.name === 'tableCell' || $from.node().type.name === 'tableHeader';
        
        // Сохраняем выделение если есть выделение ИЛИ если мы в табличной ячейке (даже пустой)
        if (hasSelection || isInTableCell) {
          savedSelection = {
            from: $from.pos,
            to: $to.pos,
            anchor: selection.anchor,
            head: selection.head,
            hasText: hasText,
            hasSelection: hasSelection,
            isInTableCell: isInTableCell
          };
        }
      }
    };

    const handleSelectionChange = () => {
      // Сохраняем выделение при изменении выделения
      const { state } = editor;
      const { selection } = state;
      const $from = selection.$from;
      const $to = selection.$to;
      
      // Проверяем, что мы в таблице - ищем table node в иерархии
      let tableNode = null;
      let depth = 1;
      while (depth <= 10 && !tableNode) {
        const node = $from.node(-depth);
        if (node && node.type.name === 'table') {
          tableNode = node;
          break;
        }
        depth++;
      }
      
      if (tableNode) {
        // Сохраняем состояние только если есть выделение или мы в ячейке с текстом
        const hasSelection = $from.pos !== $to.pos;
        const hasText = $from.node() && $from.node().textContent && $from.node().textContent.trim() !== '';
        
        // Проверяем, что мы находимся в табличной ячейке
        const isInTableCell = $from.node().type.name === 'tableCell' || $from.node().type.name === 'tableHeader';
        
        // Сохраняем выделение если есть выделение ИЛИ если мы в табличной ячейке (даже пустой)
        if (hasSelection || isInTableCell) {
          savedSelection = {
            from: $from.pos,
            to: $to.pos,
            anchor: selection.anchor,
            head: selection.head,
            hasText: hasText,
            hasSelection: hasSelection,
            isInTableCell: isInTableCell
          };
        }
      }
    };

    const handleContextMenu = (event) => {
      // Проверяем, что клик был на таблице
      const tableElement = event.target.closest('table');
      if (!tableElement) {
        if (tablePopup) {
          tablePopup.remove();
          setTablePopup(null);
        }
        return;
      }

      event.preventDefault();

      // Проверяем, есть ли сохраненное выделение
      if (!savedSelection) {
        // Пытаемся сохранить текущее выделение
        const { state } = editor;
        const { selection } = state;
        const $from = selection.$from;
        const $to = selection.$to;
        
        // Проверяем, что мы в таблице
        let tableNode = null;
        let depth = 1;
        while (depth <= 10 && !tableNode) {
          const node = $from.node(-depth);
          if (node && node.type.name === 'table') {
            tableNode = node;
            break;
          }
          depth++;
        }
        
        if (tableNode) {
          const hasSelection = $from.pos !== $to.pos;
          const hasText = $from.node() && $from.node().textContent && $from.node().textContent.trim() !== '';
          const isInTableCell = $from.node().type.name === 'tableCell' || $from.node().type.name === 'tableHeader';
          
          // Сохраняем выделение если есть выделение ИЛИ если мы в табличной ячейке (даже пустой)
          if (hasSelection || isInTableCell) {
            savedSelection = {
              from: $from.pos,
              to: $to.pos,
              anchor: selection.anchor,
              head: selection.head,
              hasText: hasText,
              hasSelection: hasSelection,
              isInTableCell: isInTableCell
            };
          }
        }
      }

      // Удаляем предыдущий попап
      if (tablePopup) {
        tablePopup.remove();
      }

      // Создаем новый попап
      const reactRenderer = new ReactRenderer(TablePopup, {
        props: {
          editor,
          position: { x: event.clientX, y: event.clientY },
          hasSelection: !!savedSelection,
          selectionData: savedSelection,
          onClose: () => {
            if (tablePopup) {
              tablePopup.remove();
              setTablePopup(null);
            }
            // Очищаем сохраненное выделение
            savedSelection = null;
          }
        },
        editor,
      });
      
      const popupElement = reactRenderer.element;
      document.body.appendChild(popupElement);
      setTablePopup(popupElement);

      // Закрываем попап при клике вне его
      const handleClickOutside = (e) => {
        if (popupElement && !popupElement.contains(e.target)) {
          popupElement.remove();
          setTablePopup(null);
          document.removeEventListener('click', handleClickOutside);
        }
      };

      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);
    };

    const editorElement = editor.view.dom;
    editorElement.addEventListener('mousedown', handleMouseDown);
    editorElement.addEventListener('contextmenu', handleContextMenu);
    
    // Подписываемся на изменения выделения в редакторе
    editor.on('selectionUpdate', handleSelectionChange);

    return () => {
      editorElement.removeEventListener('mousedown', handleMouseDown);
      editorElement.removeEventListener('contextmenu', handleContextMenu);
      editor.off('selectionUpdate', handleSelectionChange);
      if (tablePopup) {
        tablePopup.remove();
      }
    };
  }, [editor, tablePopup]);

  // Добавляем редактор в Map при создании
  useEffect(() => {
    if (editor && addEditorToMapRef.current && !editorAddedRef.current) {
      editorAddedRef.current = true;
      addEditorToMapRef.current(id, editor);
    }
    // Сбрасываем флаг при изменении id
    return () => {
      if (editorAddedRef.current) {
        editorAddedRef.current = false;
      }
    };
  }, [editor, id]); // Убрали addEditorToMap из зависимостей, используем ref

  // Обновляем содержимое редактора при изменении initialContent
  // Используем отдельный useEffect для отслеживания изменений initialContent через ref
  useEffect(() => {
    lastInitialContentRef.current = initialContent || '';
  }, [initialContent]);

  useEffect(() => {
    if (!editor) return;

    // Сбрасываем флаг при изменении id
    const currentId = String(id);
    const previousId = String(contentSetRef.current || '');

    // Получаем текущий initialContent из ref
    const currentInitialContent = lastInitialContentRef.current;

    // Обновляем только при изменении id
    if (previousId !== currentId) {
      contentSetRef.current = currentId;

      // При изменении id всегда устанавливаем содержимое один раз
      if (currentInitialContent !== '') {
        isSettingContentRef.current = true;
        try {
          editor.commands.setContent(currentInitialContent, false, {
            preserveWhitespace: 'full',
            parseOptions: {
              preserveWhitespace: 'full'
            }
          });
          lastContentRef.current = currentInitialContent;
        } catch (error) {
          console.error('Error setting content:', error);
          isSettingContentRef.current = false;
        }
      }
    }
  }, [editor, id]); // Убрали initialContent из зависимостей, используем ref

  // Late-arriving initial content: a homework container's saved HTML often
  // isn't available yet at mount time (it's an async Supabase round trip via
  // loadHomeworkData in LessonWorkspace.js) — the id-gated effect above runs
  // once immediately with initialContent still '' and no-ops, "consuming"
  // its one-shot apply before the real content ever arrived. Since `id`
  // never changes afterward, that effect never fires again, and the editor
  // stayed visibly empty forever even though editorContents (and the DB)
  // held the correct text — this was the actual cause of "even brand new
  // text is missing after refresh" for a student working solo. Catch late
  // arrivals here, but only while the editor is still empty and unfocused,
  // so this never clobbers anything the user has since typed.
  useEffect(() => {
    if (!editor) return;
    if (String(id).startsWith('homework-')) {
      console.log('[late-content-debug]', id, {
        initialContent,
        lastContent: lastContentRef.current,
        isEmpty: editor.isEmpty,
        isFocused: editor.isFocused,
      });
    }
    if (!initialContent) return;
    if (initialContent === lastContentRef.current) return;
    if (!editor.isEmpty) return;
    if (editor.isFocused) return;
    isSettingContentRef.current = true;
    try {
      editor.commands.setContent(initialContent, false, {
        preserveWhitespace: 'full',
        parseOptions: { preserveWhitespace: 'full' }
      });
      lastContentRef.current = initialContent;
    } catch (error) {
      console.error('Error applying late-arriving initial content:', error);
      isSettingContentRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, initialContent]);

  // Live content sync — mirrors the other participant's typing in real time
  // (teacher→student always; student→teacher only when Follow Mode is off,
  // see LessonWorkspace.js). Never apply while this editor is focused —
  // that wipes the local user's own edit and caret position — and never
  // just because the local content happens to differ from the remote
  // snapshot, since a student/teacher typing independently is EXPECTED to
  // diverge from the other side's older copy. Only track genuinely NEW
  // remote snapshots (the liveContent prop actually changing AFTER mount)
  // as "pending", and only apply a pending snapshot once, on the next
  // opportunity (immediately if not focused, or on blur otherwise).
  // Deliberately does NOT auto-apply whatever liveContent happens to be
  // present at mount time: switching tabs remounts this component fresh
  // with `initialContent` already holding this side's own latest text, and
  // treating the other side's (possibly older/unrelated) snapshot as
  // "pending" right away wiped that out on the very next render — the
  // actual bug reported ("student's text disappears on tab switch"). Only
  // react to updates that arrive while mounted.
  const liveContentRef = useRef(liveContent);
  const pendingRemoteRef = useRef(false);
  // When the pending snapshot arrived, and when the user last actually
  // typed locally — used below to detect a stale pending update.
  const pendingArrivedAtRef = useRef(0);
  const lastLocalEditAtRef = useRef(0);
  useEffect(() => {
    if (liveContent === liveContentRef.current) return;
    liveContentRef.current = liveContent;
    pendingRemoteRef.current = true;
    pendingArrivedAtRef.current = Date.now();
  }, [liveContent]);

  const applyLiveContent = useCallback(() => {
    if (!editor) return;
    if (!pendingRemoteRef.current) return;
    const content = liveContentRef.current;
    if (!content) { pendingRemoteRef.current = false; return; }
    // editor.isFocused is a plain boolean property on the TipTap Editor
    // instance, not a method — calling it as a function throws and was
    // being silently swallowed below, so this check never actually fired
    // and a pending remote snapshot could force-apply mid-typing.
    if (editor.isFocused) return;
    // editor.isFocused only tracks ProseMirror's OWN contentEditable DOM
    // (view.dom) — it reports false the instant focus moves to a plain
    // <input> rendered by a NodeView (e.g. ImageNodeView's text-on-image
    // input), even though the user is still actively mid-edit right there.
    // Without this check, this effect re-runs on every incoming remote
    // snapshot WHILE THAT INPUT IS FOCUSED (isFocused already false) and
    // force-replaces the whole document, wiping the in-progress image text
    // before React ever persists it to node attrs.
    if (isTypingInNestedInput()) return;
    // A pending snapshot that arrived while this editor was focused waits
    // for the next blur to be applied — but if the user kept typing in the
    // meantime, their own later edits are newer than that snapshot. Applying
    // it anyway would silently wipe out everything typed since, which is
    // exactly what happened right before Submit: clicking the button blurs
    // the editor first, triggering this apply, before the click handler
    // ever reads the (about to be overwritten) content.
    if (lastLocalEditAtRef.current > pendingArrivedAtRef.current) {
      pendingRemoteRef.current = false;
      return;
    }
    pendingRemoteRef.current = false;
    // Compare with current HTML — TipTap exposes editor.getHTML()
    let currentHtml = '';
    try { currentHtml = editor.getHTML(); } catch { return; }
    if (currentHtml === content) { lastContentRef.current = content; return; }
    isSettingContentRef.current = true;
    try {
      editor.commands.setContent(content, false, {
        preserveWhitespace: 'full',
        parseOptions: { preserveWhitespace: 'full' },
      });
      lastContentRef.current = content;
    } catch (error) {
      console.error('Error applying live content:', error);
    } finally {
      // Release the flag after a tick so onUpdate doesn't re-publish what we just set
      setTimeout(() => { isSettingContentRef.current = false; }, 0);
    }
  }, [editor]);

  useEffect(() => {
    applyLiveContent();
  }, [liveContent, applyLiveContent]);

  useEffect(() => {
    if (!editor) return;
    // A "blur" of this editor can happen because the user actually left it
    // (safe to catch up to the peer's snapshot), OR because focus moved to
    // the text-on-image <input> ImageNodeView mounts, which isn't part of
    // ProseMirror's contentEditable region — focusing it fires a real blur
    // on the editor even though the user hasn't gone anywhere. Deferred one
    // tick because at the instant blur fires, the newly-focused element
    // hasn't taken document.activeElement yet in every browser.
    // applyLiveContent() itself re-checks isTypingInNestedInput(), so this
    // handler doesn't duplicate that guard — it must NOT also skip on
    // "focus is still somewhere inside this editor's DOM" in general,
    // since that's true for entirely idle, unfocused-by-the-user editors
    // too (browsers don't auto-blur a contentEditable region just because
    // attention moves elsewhere on the page) and would otherwise block
    // every legitimate catch-up apply — e.g. a teacher's live drawing
    // never reaching a student whose editor merely still held focus from
    // an earlier, unrelated click.
    const handleBlur = () => {
      setTimeout(applyLiveContent, 0);
    };
    editor.on('blur', handleBlur);
    return () => { editor.off('blur', handleBlur); };
  }, [editor, applyLiveContent]);

  useEffect(() => {
    const hideEmptyListItems = () => {
      document.querySelectorAll('.ProseMirror li').forEach(li => {
        // Скрываем, если li содержит только <br> или пусто
        if (
          li.childNodes.length === 0 ||
          (li.childNodes.length === 1 && li.childNodes[0].nodeName === 'BR')
        ) {
          li.style.display = 'none';
        } else {
          li.style.display = '';
        }
      });
    };

    // Запускать после каждого изменения редактора
    const editor = document.querySelector('.ProseMirror');
    if (editor) {
      editor.addEventListener('input', hideEmptyListItems);
      // Первый запуск
      hideEmptyListItems();
      // Очистка
      return () => editor.removeEventListener('input', hideEmptyListItems);
    }
  }, []);

  useEffect(() => {
    const hideEmptyParagraphsInList = () => {
      document.querySelectorAll('.ProseMirror li p').forEach(p => {
        // Скрываем <p>, если он пустой или содержит только <br>
        if (
          p.childNodes.length === 0 ||
          (p.childNodes.length === 1 && p.childNodes[0].nodeName === 'BR')
        ) {
          p.style.display = 'none';
        } else {
          p.style.display = 'inline';
        }
      });
    };

    const editor = document.querySelector('.ProseMirror');
    if (editor) {
      editor.addEventListener('input', hideEmptyParagraphsInList);
      hideEmptyParagraphsInList();
      return () => editor.removeEventListener('input', hideEmptyParagraphsInList);
    }
  }, []);

  useEffect(() => {
    const removeEmptyParagraphsInList = () => {
      document.querySelectorAll('.ProseMirror li').forEach(li => {
        li.querySelectorAll('p').forEach(p => {
          if (
            p.textContent.trim() === '' &&
            ([...p.childNodes].every(n => n.nodeName === 'BR' || n.nodeType === 3))
          ) {
            p.remove();
          }
        });
      });
    };

    const editor = document.querySelector('.ProseMirror');
    if (editor) {
      editor.addEventListener('input', removeEmptyParagraphsInList);
      removeEmptyParagraphsInList();
      return () => editor.removeEventListener('input', removeEmptyParagraphsInList);
    }
  }, []);

  // Обработчик чекбоксов для конкретного редактора
  useEffect(() => {
    if (!editor) return;
    
    const handleKeyDown = (e) => {
      // Обработка двойного Enter в task list
      if (e.key === 'Enter' && e.target.closest('[data-type="taskList"]')) {
        const taskItem = e.target.closest('li');
        if (taskItem) {
          const { state } = editor;
          const { selection } = state;
          const { $from } = selection;
          
          // Проверяем, что мы в пустом task item
          if ($from.parent.content.size === 0) {
            e.preventDefault();
            // Создаем обычный параграф вместо нового task item
            editor.chain().focus().setNode('paragraph').run();
          }
        }
      }
    };
    
    const editorElement = editor.view.dom;
    editorElement.addEventListener('keydown', handleKeyDown, true);
    
    return () => {
      editorElement.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [editor]);

  // Table column resize: redistribute width between adjacent columns so outer
  // borders stay fixed. All col widths are expressed as percentages during drag
  // so the table can never overflow its 100%-wide container regardless of browser.
  useEffect(() => {
    if (!editor) return;
    const view = editor.view;
    const editorDom = view.dom;
    const MIN_COL = 48;

    let drag = null;
    let styleEl = null;

    // Writes ALL col widths as % so the browser never lets the table grow wider than 100%.
    const applyStyle = (tableId, pcts, colIdx, newPct, nextIdx, newNextPct) => {
      if (!styleEl) return;
      let css = '';
      pcts.forEach((p, i) => {
        const val = i === colIdx ? newPct : i === nextIdx ? newNextPct : p;
        css += `[data-col-drag="${tableId}"] col:nth-child(${i + 1}){width:${val.toFixed(3)}% !important}`;
      });
      styleEl.textContent = css;
    };

    const commitWidths = (table, colIdx, nextIdx, finalW, finalNextW) => {
      const { state, dispatch } = view;
      const tr = state.tr;
      table.querySelectorAll('tr').forEach(rowEl => {
        [{ idx: colIdx, w: finalW }, { idx: nextIdx, w: finalNextW }].forEach(({ idx, w }) => {
          const cell = Array.from(rowEl.querySelectorAll('td, th'))[idx];
          if (!cell) return;
          try {
            const nodePos = view.posAtDOM(cell, 0) - 1;
            const node = view.state.doc.nodeAt(nodePos);
            if (node) tr.setNodeMarkup(nodePos, null, { ...node.attrs, colwidth: [w] });
          } catch (_) {}
        });
      });
      dispatch(tr);
    };

    const onMouseDown = (e) => {
      if (!e.target.classList.contains('column-resize-handle')) return;
      const cell = e.target.closest('td, th');
      if (!cell) return;
      const table = cell.closest('table');
      if (!table) return;

      const ownCells = Array.from(cell.parentElement.querySelectorAll('td, th'));
      const colIdx = ownCells.indexOf(cell);
      if (colIdx < 0) return;
      const nextIdx = colIdx + 1;
      if (nextIdx >= ownCells.length) return;

      const firstRow = table.querySelector('tr');
      if (!firstRow) return;
      const refCells = Array.from(firstRow.querySelectorAll('td, th'));
      const tableW = table.offsetWidth || 1;
      const numCols = table.querySelectorAll('col').length;

      // Build percentage widths for ALL columns from actual rendered cell widths
      const pcts = Array.from({ length: numCols }, (_, i) =>
        ((refCells[i]?.offsetWidth ?? 0) / tableW) * 100
      );

      const startPct = pcts[colIdx];
      const nextStartPct = pcts[nextIdx];
      if (!nextStartPct) return;

      e.stopImmediatePropagation();
      e.preventDefault();

      const tableId = 'cd' + Date.now();
      table.setAttribute('data-col-drag', tableId);
      styleEl = document.createElement('style');
      document.head.appendChild(styleEl);

      drag = { table, tableId, pcts, colIdx, nextIdx, startX: e.clientX, tableW, startPct, nextStartPct };
      applyStyle(tableId, pcts, colIdx, startPct, nextIdx, nextStartPct);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };

    const onMouseMove = (e) => {
      if (!drag) return;
      const { tableId, pcts, colIdx, nextIdx, startX, tableW, startPct, nextStartPct } = drag;
      const deltaPct = (e.clientX - startX) / tableW * 100;
      const minPct = MIN_COL / tableW * 100;
      const newPct = Math.max(minPct, startPct + deltaPct);
      const newNextPct = Math.max(minPct, nextStartPct - (newPct - startPct));
      applyStyle(tableId, pcts, colIdx, newPct, nextIdx, newNextPct);
      drag.currentPct = newPct;
      drag.currentNextPct = newNextPct;
    };

    const onMouseUp = () => {
      if (!drag) return;
      const { table, colIdx, nextIdx, tableW, startPct, nextStartPct, currentPct, currentNextPct } = drag;
      drag = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      if (styleEl) { styleEl.remove(); styleEl = null; }
      table.removeAttribute('data-col-drag');
      // Convert final percentages back to pixels for ProseMirror storage
      const finalW = Math.round((currentPct ?? startPct) / 100 * tableW);
      const finalNextW = Math.round((currentNextPct ?? nextStartPct) / 100 * tableW);
      commitWidths(table, colIdx, nextIdx, finalW, finalNextW);
    };

    editorDom.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      editorDom.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (styleEl) { styleEl.remove(); styleEl = null; }
    };
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!editor) return null;

  // Helper for button style
  // eslint-disable-next-line no-unused-vars
  const btnStyle = (active) => ({
    ...toolbarBtn.base,
    ...(active ? toolbarBtn.active : {}),
  });

  // console.log(editor.schema.marks);

  return (
    <div style={{position: 'relative'}}>
      <EditorContent 
        editor={editor} 
        onFocus={() => setActiveEditor(editor)}
        style={{
          opacity: (isStudent ? isStudentLocked : lessonMode === 'view_only') ? 0.95 : 1,
        }}
      />
    </div>
  );
};

export default TextEditor;
